/**
 * ORG-CONFIG-ACCESS-AND-PRODUCT-MAPPING-V1 -- /dashboard/config/catalog-rows.
 *
 * THE CONFIGURATION SURFACE: an authorized configuration user sees the
 * catalog items this organization's treatment plans actually reference, each
 * with its stable identity, its usage context, and its CURRENT reporting-row
 * mapping -- or an explicit "Unmapped" -- and maps or remaps it to exactly ONE
 * of the org's reporting rows.
 *
 * AUTHORIZATION IS SERVER-SIDE, AND THIS FILE CONTAINS NO ROLE LOGIC. There is
 * no role list, no role comparison, and no capability decision in this
 * component: the wire is fenced by the ORGANIZATION-SCOPED CONFIG CAPABILITY
 * (config_management.authorized_roles) inside the route. A denied caller gets a
 * 401/403 from the server and this page renders the honest refusal below --
 * never a hidden control over an open door. Changing WHO may configure is a
 * config-row change; this page does not change for a different tenant.
 *
 * WHAT IT WRITES: exactly one thing -- the catalog-code -> reporting-row map
 * inside the org's owner_money_console.config value, through the gated wire.
 * Never a treatment, visit, appointment, payment, revenue, clinical record,
 * acceptance, or any money derivation.
 *
 * NEVER GUESSES: no default is preselected for an unmapped item. The picker
 * opens on "Unmapped" and stays there until a human chooses. "Other" is an
 * ordinary choice in the list, never a fallback -- an unmapped item is never
 * swept into it.
 *
 * VISUAL LAW: shared AdoraCare primitives only (PageHeader / KpiBand /
 * SectionShell / DataTable / AcBadge / AcButton / the state trio). No second
 * styling family, no bespoke table, no local CSS system.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchCatalogRowMapping,
  saveCatalogRowMapping,
} from '@/dashboard/api';
import type {
  CatalogMappingItemWire,
  CatalogMappingViewWire,
} from '@/dashboard/types';
import {
  AcBadge,
  AcButton,
  DataTable,
  EmptyState,
  ErrorState,
  KpiBand,
  LoadingState,
  PageHeader,
  SectionShell,
  type DataTableColumn,
} from '@/dashboard/ui';

/** The explicit unmapped token used by the picker (never a row key). */
export const UNMAPPED_OPTION = '__unmapped__';

/**
 * The picker's CURRENT value for one item. An unmapped item resolves to the
 * explicit UNMAPPED option -- never to the first row, never to 'other'. This
 * is the render-level half of the no-guessing law (the derivation half lives
 * in lib/domain/config/catalog-row-mapping.ts).
 */
export function pickerValue(item: CatalogMappingItemWire): string {
  return item.mappedRowKey ?? UNMAPPED_OPTION;
}

/**
 * The picker's option list: the explicit Unmapped choice FIRST, then the org's
 * own reporting rows in sheet order. 'Other' appears only as an ordinary row
 * an operator may deliberately choose.
 */
export function mappingOptions(
  rows: ReadonlyArray<{ key: string; label: string }>,
): ReadonlyArray<{ value: string; label: string }> {
  return [
    { value: UNMAPPED_OPTION, label: 'Unmapped' },
    ...rows.map((r) => ({ value: r.key, label: r.label })),
  ];
}

/** Display label for a code-less item, so the row never reads as blank. */
export function itemDisplayName(item: CatalogMappingItemWire): string {
  if (item.procedureName.trim() !== '') return item.procedureName;
  return item.uncoded ? 'Free-typed procedure' : item.catalogCode;
}

/**
 * The surface's own honest refusal. A configuration surface that renders an
 * empty worksheet when the caller lacks the capability would teach an operator
 * that the org has no catalog items -- a lie. It says WHAT happened and WHO to
 * ask instead.
 */
export function DeniedState(): JSX.Element {
  return (
    <EmptyState
      title="You do not have configuration access"
      body={
        <>
          Managing reporting configuration is granted per organization. Ask an
          administrator to grant your role the configuration capability for this
          organization.
        </>
      }
      testId="catalog-mapping-denied"
    />
  );
}

export default function CatalogRowMappingPage(): JSX.Element {
  const [view, setView] = useState<CatalogMappingViewWire | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'denied' | 'error'>(
    'loading',
  );
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState((prev) => (prev === 'ready' ? 'ready' : 'loading'));
    try {
      const data = await fetchCatalogRowMapping();
      setView(data);
      setState('ready');
    } catch (err) {
      // The capability refusal is a DIFFERENT outcome from a fault, and the
      // operator deserves to be told which one happened.
      const status = (err as { status?: number } | null)?.status;
      const message = String((err as { message?: string } | null)?.message ?? '');
      const denied =
        status === 401 ||
        status === 403 ||
        /capability|unauthor|forbidden/i.test(message);
      setState(denied ? 'denied' : 'error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onPick = useCallback(
    async (item: CatalogMappingItemWire, choice: string) => {
      setSavingCode(item.catalogCode);
      setSaveError(null);
      try {
        const next = await saveCatalogRowMapping({
          catalogCode: item.catalogCode,
          rowKey: choice === UNMAPPED_OPTION ? null : choice,
        });
        setView(next);
      } catch (err) {
        // Never leave a stale optimistic value on screen: the worksheet still
        // shows the SERVER's last known truth, and the fault is named.
        setSaveError(
          String(
            (err as { message?: string } | null)?.message ??
              'The mapping could not be saved.',
          ),
        );
      } finally {
        setSavingCode(null);
      }
    },
    [],
  );

  const columns: ReadonlyArray<DataTableColumn<CatalogMappingItemWire>> =
    useMemo(
      () => [
        {
          key: 'item',
          header: 'Catalog item',
          render: (item) => (
            <div className="cfgmap__item">
              <strong>{itemDisplayName(item)}</strong>
              <span className="cfgmap__code">
                {item.uncoded ? 'No catalog code' : item.catalogCode}
              </span>
            </div>
          ),
        },
        {
          key: 'usage',
          header: 'Used',
          align: 'end',
          render: (item) => (
            <span title="Accepted items / all plan items for this code">
              {item.acceptedItemCount} accepted / {item.itemCount}
            </span>
          ),
        },
        {
          key: 'status',
          header: 'Reporting row',
          render: (item) =>
            item.mappedRowKey === null ? (
              <AcBadge tone="pending" testId="mapping-unmapped">
                Unmapped
              </AcBadge>
            ) : (
              <AcBadge tone="positive">{item.mappedRowLabel}</AcBadge>
            ),
        },
        {
          key: 'pick',
          header: 'Map to',
          render: (item) => (
            <label className="cfgmap__pick">
              <span className="ac-sr-only">
                Reporting row for {itemDisplayName(item)}
              </span>
              <select
                className="cfgmap__select"
                value={pickerValue(item)}
                disabled={savingCode !== null}
                data-testid={`mapping-select-${item.catalogCode}`}
                onChange={(e) => {
                  void onPick(item, e.target.value);
                }}
              >
                {/* No row is preselected for an unmapped item: the picker
                    opens on Unmapped and never guesses a pairing. */}
                {mappingOptions(view?.rows ?? []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          ),
        },
      ],
      [onPick, savingCode, view],
    );

  if (state === 'loading') {
    return <LoadingState label="Loading configuration..." lines={4} />;
  }
  if (state === 'denied') {
    return (
      <>
        <PageHeader
          eyebrow="Configuration"
          title="Reporting row mapping"
          testId="catalog-mapping-header"
        />
        <DeniedState />
      </>
    );
  }
  if (state === 'error' || view === null) {
    return (
      <ErrorState
        title="The configuration worksheet could not be loaded"
        action={
          <AcButton onClick={() => void load()} variant="secondary">
            Retry
          </AcButton>
        }
        testId="catalog-mapping-error"
      />
    );
  }

  const unmapped = view.items.filter((i) => i.mappedRowKey === null);

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Reporting row mapping"
        subs={[
          'Choose which reporting row each catalog item belongs to. Money for an unmapped item is reported separately and is never assigned to a row by guesswork.',
        ]}
        testId="catalog-mapping-header"
      />

      <KpiBand
        title="Mapping status"
        testId="catalog-mapping-kpis"
        cells={[
          {
            key: 'unmapped',
            label: 'Unmapped items',
            value: String(view.unmappedCount),
            meta: 'Their revenue is reported as unattributed until mapped',
          },
          {
            key: 'mapped',
            label: 'Mapped items',
            value: String(view.mappedCount),
          },
          {
            key: 'rows',
            label: 'Reporting rows',
            value: String(view.rows.length),
          },
        ]}
      />

      {saveError !== null ? (
        <ErrorState
          title="That mapping was not saved"
          detail={saveError}
          action={
            <AcButton onClick={() => void load()} variant="secondary">
              Reload
            </AcButton>
          }
          testId="catalog-mapping-save-error"
        />
      ) : null}

      {view.capped ? (
        <p className="cfgmap__note" data-testid="catalog-mapping-capped">
          Showing a bounded sample of this organization&apos;s plan items; some
          rarely used catalog items may not be listed.
        </p>
      ) : null}

      {/* Unmapped items lead the page (the founder's "prominently"), and the
          full worksheet follows so a mapped item can always be corrected. */}
      <SectionShell
        title="Needs a reporting row"
        testId="catalog-mapping-unmapped-section"
      >
        {unmapped.length === 0 ? (
          <EmptyState
            title="Every catalog item has a reporting row"
            body="Nothing is waiting on a mapping decision."
            testId="catalog-mapping-none-unmapped"
          />
        ) : (
          <DataTable
            columns={columns}
            rows={unmapped}
            rowKey={(item) => item.catalogCode}
            label="Catalog items awaiting a reporting row"
            testId="catalog-mapping-unmapped-table"
          />
        )}
      </SectionShell>

      <SectionShell
        title="All catalog items"
        testId="catalog-mapping-all-section"
      >
        <DataTable
          columns={columns}
          rows={view.items}
          rowKey={(item) => item.catalogCode}
          label="All catalog items and their reporting rows"
          empty={
            <EmptyState
              title="No catalog items found for this organization"
              body="Catalog items appear here once treatment plans reference them."
              testId="catalog-mapping-empty"
            />
          }
          testId="catalog-mapping-all-table"
        />
      </SectionShell>
    </>
  );
}
